import React, { FC } from 'react';

import ReactHtmlParser from 'react-html-parser';
import { mqMin } from '@lucid/theme';

import Box from '../box/box';
import Heading from '../heading/heading';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Image from '../image/image';
import Link, { LinkProps } from '../link/link';
import Text from '../text/text';
import VisibleLottie from '../lottie/visible-lottie';
import { ComponentImage, ComponentLink } from '../../types/generics';
import { LucidTheme } from '../../types/types';

export interface BackNForthSectionProps {
  animation?: {
    data: string | null;
    alt: string | null;
  };
  backgroundImage?: string;
  backgroundImageAlt?: string;
  cta?: ComponentLink;
  description: {
    processed?: string;
  };
  image: ComponentImage;
  logo?: ComponentImage;
  title?: string;
}

export interface BackNForthProps {
  /** Reverses the image and text for each component. */
  isReversed?: boolean;
  /** The content for each section. */
  sections: BackNForthSectionProps[];
  /** Sets the images to be wide. */
  wideImages?: boolean;
  /** Passes the specific Link component props to the Link component */
  linkProps?: LinkProps;
}

const BackNForth: FC<BackNForthProps> = ({ isReversed = false, sections, wideImages = false, linkProps }) => {
  // TODO: Create a reusable filtering method that checks empty fields
  const bnfSections = sections.filter(section => {
    return section.description.processed && (section.image.imageSrc || section.animation?.data);
  });

  return (
    <Box as="section" css={{ label: 'sections' }} pb={{ default: 4, tabletSmall: 10 }}>
      {bnfSections.map((section, index) => {
        const { animation, backgroundImage, backgroundImageAlt, cta, description, image, logo, title } = section;
        const isOdd = isReversed ? index % 2 === 1 : index % 2 === 0;
        return (
          <Box
            key={index}
            as="section"
            css={(theme: LucidTheme) => [
              {
                label: 'backNForth',
                display: 'grid',
                position: 'relative',
                gridTemplateColumns: '1fr',
                gridTemplateRows: '1fr auto',
                gridTemplateAreas: '"image" "content"',
                paddingTop: theme.space.s104,
                [mqMin.tabletSmall]: {
                  gridTemplateRows: '1fr',
                },
              },
              isOdd && {
                [mqMin.tabletSmall]: {
                  gridTemplateAreas: '"content image"',
                  gridTemplateColumns: wideImages ? '0.65fr 1fr' : '0.85fr 1fr',
                },
              },
              !isOdd && {
                [mqMin.tabletSmall]: {
                  gridTemplateAreas: '"image content"',
                  gridTemplateColumns: wideImages ? '1fr 0.5fr' : '1fr 0.85fr',
                },
              },
            ]}
            id={`section-${index + 1}`}
          >
            <Box
              css={{
                label: 'sectionImageContainer',
                gridArea: 'image',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: '460px',
              }}
            >
              {animation?.data ? (
                <VisibleLottie alt={animation.alt!} data={animation.data!} />
              ) : (
                <ImageWithPlaceholder
                  alt={image.imageAlt || ''}
                  aspectRatio="1:1"
                  css={{
                    '& > .image': {
                      maxHeight: '460px',
                      maxWidth: '580px',
                    },
                  }}
                  src={image.imageSrc!}
                />
              )}
              {backgroundImage && (
                <Image
                  css={(theme: LucidTheme) => [
                    {
                      maxWidth: '200vw',
                      maxHeight: '400px',
                      objectFit: 'cover',
                      position: 'absolute',
                      zIndex: theme.zIndices[0],
                      [mqMin.tabletSmall]: {
                        maxWidth: wideImages ? '80%' : '60%',
                      },
                    },
                    isOdd && {
                      [mqMin.tabletSmall]: {
                        right: -theme.space[3],
                      },
                    },
                    !isOdd && {
                      [mqMin.tabletSmall]: {
                        left: -theme.space[3],
                      },
                    },
                  ]}
                  imageAlt={backgroundImageAlt || ''}
                  imageSrc={backgroundImage}
                />
              )}
            </Box>
            <Box
              css={(theme: LucidTheme) => [
                {
                  label: 'sectionContent',
                  gridArea: 'content',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: `${theme.space[5]}px 0`,
                  maxWidth: '480px',
                  [mqMin.tabletSmall]: {
                    maxWidth: 'initial',
                    alignSelf: 'center',
                  },
                },
                isOdd && {
                  textAlign: 'right',
                  justifySelf: 'end',
                  [mqMin.tabletSmall]: {
                    paddingRight: theme.space[4],
                  },
                },
                !isOdd && {
                  textAlign: 'left',
                  justifySelf: 'start',
                  [mqMin.tabletSmall]: {
                    paddingLeft: theme.space[4],
                  },
                },
              ]}
            >
              {!logo?.imageSrc ? (
                <Heading
                  as="h2"
                  css={(theme: LucidTheme) => [
                    {
                      marginBottom: theme.space[4],
                    },
                    isOdd && {
                      textAlign: 'right',
                    },
                    !isOdd && {
                      textAlign: 'left',
                    },
                  ]}
                  variant="headline2XL"
                >
                  {title}
                </Heading>
              ) : (
                <Image
                  css={(theme: LucidTheme) => [
                    {
                      maxHeight: '80px',
                      maxWidth: '200px',
                      marginBottom: theme.space[4],
                    },
                    isOdd && {
                      marginLeft: 'auto',
                    },
                    !isOdd && {
                      marginRight: 'auto',
                    },
                    logo.imageAlt?.toLowerCase()?.includes('zoom') && {
                      maxHeight: '40px',
                    },
                  ]}
                  imageAlt={logo.imageAlt || ''}
                  imageSrc={logo.imageSrc!}
                  loading="lazy"
                />
              )}
              <Text variant="paragraphS400">{ReactHtmlParser(description?.processed!)}</Text>
              {cta?.linkText && (
                <Link
                  {...linkProps}
                  hasArrow
                  css={[isOdd && { textAlign: 'right' }, !isOdd && { textAlign: 'left' }]}
                  to={cta?.linkUrl}
                >
                  {cta.linkText}
                </Link>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export { BackNForth };
export default BackNForth;
